import React from "react";
import { Pencil, Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useGetApplicationQuery, usePostUpsertApplicationMutation, usePutHomeMutation } from "@/store/service";
import { useAuth } from "@/context/AuthContext";
import User from "@/model/interface/User";
import { ApplicationStep } from "@/enum/ApplicationStep";
import { isDebugForms, getDebugNextStepUrl } from "@/util/debug";
import { userSchema } from "@/model/schemas";
import { zodFormResolver } from "@/util/form";
import Skeleton from "react-loading-skeleton";

export const StepTypes = [
  "preview",
  "applicant",
  "liabilities",
  "assets",
  "banking-details",
  "review",
  "success",
] as const;
export type StepTypes = (typeof StepTypes)[number];

const ApplicationUser = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { user: userData } = useAuth();
  const { data: application, isLoading } = useGetApplicationQuery(params.id!);

  const [postUser] = usePutHomeMutation();
  const [postApplication] = usePostUpsertApplicationMutation();

  const { register, formState, handleSubmit, getValues } = useForm<User>({
    resolver: zodFormResolver<User>(userSchema),
    mode: "onTouched",
    defaultValues: {},
  });

  const onSubmit = async (data: User) => {
    await postUser({ ...data, id: userData?.id });
    await postApplication({ id: params.id!, step: ApplicationStep.Applicant });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDebugForms()) {
      navigate(getDebugNextStepUrl(params.id!, ApplicationStep.User));
    } else {
      handleSubmit(onSubmit)(e);
    }
  };

  if (isLoading || !userData) {
    return <Skeleton count={5} height="2.75em" className="mt-4" />;
  }

  if (!isLoading && !(params.id && userData)) {
    navigate("/");
    return null;
  }

  const checkIcon = () => {
    return <Check className="inline size-4 text-green-600" />;
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="w-full max-w-2xl mx-auto space-y-5">
        <h3 className="text-2xl font-semibold text-gray-900 text-center">User details</h3>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">Email</h6>
          <input
            type="text"
            disabled
            value={userData.email}
            className="w-full h-11 rounded-lg border border-gray-200 bg-gray-50 px-4 pr-10 text-sm outline-none text-gray-500"
            placeholder="Email"
            required={true}
          />
        </div>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">First name</h6>
          <input
            type="text"
            {...register("firstName", { value: userData.firstName })}
            className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${formState.errors.firstName ? "border-red-400" : "border-gray-300"}`}
            placeholder="First Name"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors.firstName && (
            <p className="mt-1 text-sm text-red-500">{formState.errors.firstName.message}</p>
          )}
        </div>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">Last name</h6>
          <input
            type="text"
            {...register("lastName", { value: userData.lastName })}
            className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${formState.errors.lastName ? "border-red-400" : "border-gray-300"}`}
            placeholder="Last Name"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors.lastName && (
            <p className="mt-1 text-sm text-red-500">{formState.errors.lastName.message}</p>
          )}
        </div>
        {!userData.hasPassword && (
          <div className="relative">
            <h6 className="text-sm text-gray-500 mb-1">Password</h6>
            <input
              type="password"
              {...register("password")}
              autoComplete="new-password"
              className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${formState.errors.password ? "border-red-400" : "border-gray-300"}`}
              placeholder="Password"
            />
            <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
            {formState.errors.password && (
              <p className="mt-1 text-sm text-red-500">{formState.errors.password.message}</p>
            )}
          </div>
        )}
        <div className="flex justify-center pt-2">
          <button
            type="submit"
            name="submit"
            className="inline-flex items-center rounded-full bg-[#9BFBE0] px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
          >
            Continue
          </button>
        </div>
      </div>
    </form>
  );
};

ApplicationUser.propTypes = {};

export default ApplicationUser;
